import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import React, { Suspense, lazy } from "react";
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import "./App.scss";
import { LogoutModal } from "./components/global/LogoutModal";

// Pages - lazy load routes that are not needed on initial load
import HomePage from "./pages/HomePage";
import SharedLayout from "./pages/SharedLayout";
import ReportComparatorPage from "./pages/ReportComparatorPage";

// Lazy load authenticated pages for code splitting
const ForecastsPage = lazy(() => import("./pages/ForecastsPage"));
const DemandForecastPage = lazy(() => import("./pages/DemandForecastPage"));
const AllocationCheckListPage = lazy(
  () => import("./pages/AllocationCheckListPage"),
);
const RetinaForecastPage = lazy(() => import("./pages/RetinaForecastPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));

// Context Providers
import { AppContextProvider, useAppContext } from "./contexts/AppContext";
import { UserProfileProvider } from "./contexts/UserProfileContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { GlobalMessageProvider } from "./contexts/GlobalMessageContext";
import { AppRouteContextProvider } from "./contexts/AppRouteContext";
import { SiteProvider } from "./contexts/SiteContext";

// Others
import { ErrorBoundary } from "react-error-boundary";
import GlobalErrorFallback from "./components/global/GlobalErrorFallback";
import AuthenticatedAppWrapper from "./components/global/AuthenticatedAppWrapper";
import ModuleRedirect from "./components/global/ModuleRedirect";
import useEnvironment from "./hooks/useEnvironment";

// Component that handles the loading state and renders routes only when data is ready
const AuthenticatedRoutesWrapper: React.FC = () => {
  return (
    <GlobalMessageProvider>
      <AppContextProvider>
        <UserProfileProvider>
          <SettingsProvider>
            <AppRouteContextProvider>
              <SiteProvider>
                <AuthenticatedAppWrapper>
                  <SharedLayout />
                </AuthenticatedAppWrapper>
              </SiteProvider>
            </AppRouteContextProvider>
          </SettingsProvider>
        </UserProfileProvider>
      </AppContextProvider>
    </GlobalMessageProvider>
  );
};

const ForecastsRoute: React.FC = () => {
  const { isProd, isLoading } = useEnvironment();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isProd) {
    return <ModuleRedirect />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <ForecastsPage />
    </Suspense>
  );
};

const PageLoader = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
    }}
  >
    <div>Loading...</div>
  </div>
);

const ReportComparatorRoute: React.FC = () => {
  const { isProd, isPreprod, isLoading } = useEnvironment();
  const { userContextData } = useAppContext();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isProd || !isPreprod || !userContextData?.IsInternalRole) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <ReportComparatorPage />
    </Suspense>
  );
};

const App: React.FC = () => {
  const unAuthenticatedRoutes = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        <Route index element={<HomePage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Route>,
    ),
  );

  const authenticatedRoutes = createBrowserRouter(
    createRoutesFromElements(
      <Route errorElement={<GlobalErrorFallback />}>
        <Route path="/" element={<AuthenticatedRoutesWrapper />}>
          <Route index element={<ModuleRedirect />} />
          <Route path="forecasts" element={<ForecastsRoute />} />
          <Route
            path="demand-forecast"
            element={
              <Suspense fallback={<PageLoader />}>
                <DemandForecastPage />
              </Suspense>
            }
          />
          <Route
            path="allocation-check"
            element={
              <Suspense fallback={<PageLoader />}>
                <AllocationCheckListPage />
              </Suspense>
            }
          />
          <Route
            path="retina-forecast"
            element={
              <Suspense fallback={<PageLoader />}>
                <RetinaForecastPage />
              </Suspense>
            }
          />
          <Route
            path="report-comparator"
            element={<ReportComparatorRoute />}
          />
          <Route
            path="settings"
            element={
              <Suspense fallback={<PageLoader />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route path="*" element={<ModuleRedirect />} />
        </Route>
      </Route>,
    ),
  );

  return (
    <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
      <UnauthenticatedTemplate>
        <RouterProvider router={unAuthenticatedRoutes} />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <LogoutModal />
        <RouterProvider router={authenticatedRoutes} />
      </AuthenticatedTemplate>
    </ErrorBoundary>
  );
};

export default App;
